/**
 * Stream Broadcast Service
 * 
 * Uses BroadcastChannel API for local inter-window communication.
 * This enables the stream connector popup to send events to the game play window.
 * 
 * 100% Local - no network traffic, messages stay in browser memory.
 */

import {
  BroadcastEvent,
  BroadcastEventType,
  StreamingComment,
  StreamingGift,
  StreamingLike,
  StreamingMetrics,
  StreamingRoomInfo,
  ConnectionState,
  StreamingPlatform
} from '../../types/streaming';

const CHANNEL_NAME = 'beemi-stream-events';

export interface ConnectionStatePayload {
  state: ConnectionState;
  platform: StreamingPlatform | null;
  username: string | null;
  title?: string | null;
  streamImageUrl?: string | null;
  profileImageUrl?: string | null;
}

type BroadcastEventHandler = (event: BroadcastEvent) => void;

class StreamBroadcastService {
  private channel: BroadcastChannel | null = null;
  private handlers: Set<BroadcastEventHandler> = new Set();
  private isInitialized = false;

  /**
   * Initialize the broadcast channel
   */
  initialize(): void {
    if (this.isInitialized || typeof BroadcastChannel === 'undefined') {
      return;
    }

    try {
      this.channel = new BroadcastChannel(CHANNEL_NAME);
      
      this.channel.onmessage = (event: MessageEvent<BroadcastEvent>) => {
        console.log('📨 [StreamBroadcast] Received via BroadcastChannel:', event.data?.type);
        this.notifyHandlers(event.data);
      };

      this.channel.onmessageerror = (event) => {
        console.error('[StreamBroadcast] Message error:', event);
      };

      this.isInitialized = true;
      console.log('[StreamBroadcast] ✅ Initialized on channel:', CHANNEL_NAME);
    } catch (error) {
      console.error('[StreamBroadcast] Failed to initialize:', error);
    }
  }

  /**
   * Subscribe to broadcast events
   */
  subscribe(handler: BroadcastEventHandler): () => void {
    this.initialize();
    this.handlers.add(handler);
    
    // Return unsubscribe function
    return () => {
      this.handlers.delete(handler);
    };
  }

  /**
   * Notify all handlers of an event
   */
  private notifyHandlers(event: BroadcastEvent): void {
    this.handlers.forEach(handler => {
      try {
        handler(event);
      } catch (error) {
        console.error('[StreamBroadcast] Handler error:', error);
      }
    });
  }

  /**
   * Publish a raw platform event (no mapping/transformation)
   * This goes directly to stream-parser
   */
  publishRawEvent(eventType: 'comment' | 'gift' | 'like' | 'join' | 'follow' | 'share' | 'stats', rawEvent: any, platform: string): void {
    console.log(`📡 [StreamBroadcast] Publishing raw ${eventType} from ${platform}`, rawEvent?.type || 'no-euler-type');
    this.publish('raw-stream-event', { eventType, rawEvent, platform });
  }

  /**
   * Publish a comment event (DEPRECATED - use publishRawEvent)
   * @deprecated Use publishRawEvent for direct parser integration
   */
  publishComment(comment: StreamingComment): void {
    this.publish('stream-comment', comment);
  }

  /**
   * Publish a gift event (DEPRECATED - use publishRawEvent)
   * @deprecated Use publishRawEvent for direct parser integration
   */
  publishGift(gift: StreamingGift): void {
    this.publish('stream-gift', gift);
  }

  /**
   * Publish a like event (DEPRECATED - use publishRawEvent)
   * @deprecated Use publishRawEvent for direct parser integration
   */
  publishLike(like: StreamingLike): void {
    this.publish('stream-like', like);
  }

  /**
   * Publish metrics update
   */
  publishMetrics(metrics: StreamingMetrics): void {
    this.publish('stream-metrics', metrics);
  }

  /**
   * Publish connection state change
   */
  publishConnectionState(payload: ConnectionStatePayload): void {
    this.publish('connection-state', payload);
  }

  /**
   * Publish room info update
   */
  publishRoomInfo(roomInfo: StreamingRoomInfo): void {
    this.publish('room-info', roomInfo);
  }

  /**
   * Request current state from any active stream windows
   */
  requestState(): void {
    this.publish('request-state', { timestamp: Date.now() });
  }

  /**
   * Respond with current state
   */
  respondWithState(state: any): void {
    this.publish('state-response', state);
  }

  /**
   * Generic publish method
   */
  private publish(type: BroadcastEventType, payload: any): void {
    this.initialize();
    
    if (!this.channel) {
      return;
    }

    const event: BroadcastEvent = {
      type,
      payload,
      timestamp: Date.now()
    };

    try {
      this.channel.postMessage(event);
    } catch (error) {
      console.error('[StreamBroadcast] Failed to publish:', error);
    }
  }

  /**
   * Close the broadcast channel
   */
  close(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }
    this.handlers.clear();
    this.isInitialized = false;
  }

  /**
   * Check if BroadcastChannel is supported
   */
  isSupported(): boolean {
    return typeof BroadcastChannel !== 'undefined';
  }
}

export const streamBroadcastService = new StreamBroadcastService();

